/**
 * Реестр живых RTCPeerConnection через monkey-patch конструктора.
 *
 * Goloom SDK не отдаёт свой PC наружу, поэтому мы оборачиваем
 * `window.RTCPeerConnection` при старте фичи: каждый созданный инстанс
 * добавляется в Set, а `pc.close()` его оттуда удаляет. Идемпотентность
 * обеспечивается маркером на обёртке — повторный вызов ничего не делает.
 * Параметры конструктора проходят насквозь — никаких ICE-серверов / SDP
 * мы не читаем и не сохраняем.
 */
const PATCH_MARKER = '__transcoderObservabilityPatched' as const;

type Patchable = typeof RTCPeerConnection & { [PATCH_MARKER]?: true };

const live = new Set<RTCPeerConnection>();

export function patchRTCPeerConnection(): void {
    if (typeof window === 'undefined') {
        return;
    }

    const Original = window.RTCPeerConnection as Patchable | undefined;
    if (!Original || Original[PATCH_MARKER]) {
        return;
    }

    function Patched(this: RTCPeerConnection, ...args: unknown[]) {
        // @ts-ignore variadic constructor forward
        const pc: RTCPeerConnection = new Original(...args);
        live.add(pc);

        const originalClose = pc.close.bind(pc);
        pc.close = () => {
            live.delete(pc);
            originalClose();
        };

        return pc;
    }

    Patched.prototype = Original.prototype;
    Object.setPrototypeOf(Patched, Original);
    (Patched as unknown as Patchable)[PATCH_MARKER] = true;

    try {
        window.RTCPeerConnection = Patched as unknown as typeof RTCPeerConnection;
    } catch {
        // У части пользователей `window.RTCPeerConnection` залочен анти-WebRTC
        // расширением (uBlock с «Prevent WebRTC from leaking local IPs», AdGuard
        // и т.п. через `Object.defineProperty({ writable: false })`). Свойство
        // не наше — observability транскодера best-effort, тихо пропускаем.
    }
}

export function getLivePeerConnections(): RTCPeerConnection[] {
    return Array.from(live);
}
